import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';

import { IOrder, IOrderSearch } from '@models/orders/order';
import { IOrderAccount } from '@models/orders/order-master';
import { IOrderLineItem } from '@models/orders/order-line-items';
import { IOrderElement } from '@models/orders/order-element';
import { HTTPPagination, HTTPResponse, HTTPSearchResponse, APP_CONFIG } from '@mavles/shared';
import { ContactApiService, IContact } from '@mavles/contacts';
import { OrderElementService } from './order-element.service';

@Injectable({
  providedIn: 'root'
})
export class OrderApiService {
  private http = inject(HttpClient);
  private appConfig = inject(APP_CONFIG);
  private formBuilder = inject(FormBuilder);
  private elementService = inject(OrderElementService);
  private contactService = inject(ContactApiService);

  private readonly orderAccountAPI = this.appConfig.apiUrl + 'accounts/orders/';
  private readonly orderElementAPI = this.appConfig.apiUrl + 'accounts/elements/';

  orderAccounts: IOrderAccount[] = [];
  paginatedOrders!: HTTPPagination<IOrderSearch[]>;

  protected get orderwithElementAPI() {
    return this.appConfig.apiUrl + 'orders/' + this.elementService.getCurrentElementString() + '/';
  }

  protected get orderwithElementAPIReport() {
    return this.appConfig.apiUrl + 'report/' + this.elementService.getCurrentElementString() + '/';
  }

  protected get orderAPI() {
    return this.appConfig.apiUrl + 'orders/';
  }

  getContactsForCurrentPage(): IContact[] {
    const contactTypes: number[] | undefined = this.elementService.getCurrentElement()?.contact_types?.map(i => i.contact_type_id);
    return this.contactService.getAllByContactType(contactTypes);
  }

  getOrderAccountByID(account_id: number): IOrderAccount | undefined {
    return this.orderAccounts.find(a => a.account_id === account_id);
  }

  getAllOrderAccounts() {
    this.http.get<HTTPResponse<IOrderAccount[]>>(this.orderAccountAPI + 'get-all').subscribe(response => {
      this.orderAccounts = response.data;
    });
  }

  saveOrderAccount(account: IOrderAccount): Observable<HTTPResponse<IOrderAccount>> {
    if (account.account_id) {
      return this.http.post<HTTPResponse<IOrderAccount>>(this.orderAccountAPI + 'update', account);
    }
    return this.http.post<HTTPResponse<IOrderAccount>>(this.orderAccountAPI + 'add', account);
  }

  deleteOrderAccount(account_id: number): Observable<HTTPResponse<any>> {
    return this.http.delete<HTTPResponse<any>>(this.orderAccountAPI + 'delete/' + account_id);
  }

  getNewOrderNumber(): Observable<HTTPResponse<any>> {
    return this.http.get<HTTPSearchResponse<IOrderSearch[]>>(this.orderwithElementAPI + 'get-new-num');
  }

  getOrderDetails(order_id: number): Observable<HTTPResponse<IOrderSearch>> {
    return this.http.get<HTTPResponse<IOrderSearch>>(this.orderwithElementAPI + 'get/' + order_id);
  }

  getOrderDetailsForContact(orderElement: string | undefined, contact_id: number): Observable<HTTPResponse<IOrderSearch[]>> {
    if (orderElement) {
      return this.http.get<HTTPResponse<IOrderSearch[]>>(`${this.orderAPI}${orderElement}/get/contact/${contact_id}`);
    }
    return of({} as HTTPResponse<IOrderSearch[]>);
  }

  getAllOrdersByContactID(contact_id: number, orderElement: string = 'invoice'): Observable<HTTPResponse<IOrder[]>> {
    return this.http.get<HTTPResponse<IOrder[]>>(`${this.orderAPI}${orderElement}/get/contact/${contact_id}`);
  }

  getOrderItemForm(items: IOrderLineItem): FormGroup {
    const form = this.formBuilder.group({
      line_id: [items.line_id, []],
      order_id: [items.order_id, []],
      product_id: [items.product_id, [Validators.required]],
      line_quantity: [items.line_quantity, [Validators.required]],
      line_price: [items.line_price, [Validators.required, Validators.min(1)]],
      line_amount: [items.line_amount, []],
      line_order: [items.line_order, []],
      line_description: [items.line_description, []],
    });

    const updateLineAmount = (form: FormGroup) => {
      const quantity = form.get('line_quantity')?.value || 0;
      const price = form.get('line_price')?.value || 0;
      const amount = quantity * price;
      form.get('line_amount')?.setValue(amount, { emitEvent: false });
    };

    form.get('line_quantity')?.valueChanges.subscribe(() => updateLineAmount(form));
    form.get('line_price')?.valueChanges.subscribe(() => updateLineAmount(form));

    return form;
  }

  search(params: any): Observable<HTTPSearchResponse<IOrderSearch[]>> {
    return this.http.get<HTTPSearchResponse<IOrderSearch[]>>(this.orderwithElementAPI + 'search', { params });
  }

  saveOrderDetails(order: IOrder): Observable<HTTPResponse<IOrderSearch>> {
    if (order.order_id) {
      return this.http.post<HTTPResponse<IOrderSearch>>(this.orderwithElementAPI + 'update', order);
    }
    return this.http.post<HTTPResponse<IOrderSearch>>(this.orderwithElementAPI + 'add', order);
  }

  getAllOrderElements(): Observable<HTTPResponse<IOrderElement[]>> {
    return this.http.get<HTTPResponse<IOrderElement[]>>(this.orderElementAPI + 'get-all');
  }

  downloadOrderPDF(orderId: number): Observable<Blob> {
    const headers = new HttpHeaders({ Accept: 'application/pdf' });
    return this.http.get(`${this.orderwithElementAPI}order-report/${orderId}`, { headers, responseType: 'blob' });
  }

  downloadOrderDeliveryNotePDF(orderId: number): Observable<Blob> {
    const headers = new HttpHeaders({ Accept: 'application/pdf' });
    return this.http.get(`${this.orderwithElementAPIReport}order-delivery-note/${orderId}`, { headers, responseType: 'blob' });
  }

  downloadOrderEstimatePDF(orderId: number): Observable<Blob> {
    const headers = new HttpHeaders({ Accept: 'application/pdf' });
    return this.http.get(`${this.orderwithElementAPIReport}order-estimate/pdf/${orderId}`, { headers, responseType: 'blob' });
  }
}
